import { type RefObject, useEffect, useLayoutEffect, useState } from "react";

const read = (key: string) => localStorage.getItem(key) ?? "";
const write = (key: string, text: string) =>
  text ? localStorage.setItem(key, text) : localStorage.removeItem(key);

// Chat keeps the existing chat storage namespace so saved drafts remain available.
const draftStorageKey = (scope: string) =>
  `draft:${scope.replace(/^\/(chat|home|work):/, "/chats:")}`;

export function readDraft(scope: string) {
  return read(draftStorageKey(scope));
}

export function clearDraft(scope: string) {
  write(draftStorageKey(scope), "");
}

export function restoreDraft(scope: string, text: string) {
  const key = draftStorageKey(scope);
  write(key, read(key) || text);
}

export function useDraft(scope: string, initialValue = "") {
  const key = draftStorageKey(scope);
  const [value, setValue] = useState(() => read(key) || initialValue);
  const [draftKey, setDraftKey] = useState(key);
  if (draftKey !== key) {
    setDraftKey(key);
    setValue(read(key) || initialValue);
  }
  useEffect(() => write(key, value), [key, value]);
  const clear = () => write(key, "");
  return [value, setValue, clear] as const;
}

export function useCaretToEndOnMount(
  ref: RefObject<HTMLTextAreaElement | null>
) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) el.setSelectionRange(el.value.length, el.value.length);
  }, [ref]);
}
